import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API_URL } from '../services/api';
import { useToast } from '../components/ToastContainer';
import ImageViewer from '../components/ImageViewer';

interface Option {
  id: number;
  text: string;
  is_correct: boolean;
}

interface QuestionImage {
  image_file: string;
  caption?: string;
  order_num: number;
}

interface Question {
  id: number;
  order_num: number;
  text: string;
  question_type: 'multiple_choice' | 'text' | 'audio_response';
  is_active: boolean;
  allow_multiple_answers?: boolean;
  requires_correct_answer?: boolean;
  options: Option[];
  images: QuestionImage[];
  correct_answers: any; // For multiple choice: array of correct options; for text with auto-grade: the correct answer
  answer_key_text: string; // Admin-entered answer key notes
  is_gradable: boolean;
}

interface Quiz {
  id: number;
  title: string;
  description: string;
  category?: string;
}

interface AnswerKeyData {
  quiz: Quiz;
  questions: Question[];
  summary: {
    total_questions: number;
    gradable_questions: number;
    active_questions: number;
  };
}

const AnswerKeyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [data, setData] = useState<AnswerKeyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track edited answers: { questionId: correctAnswer }
  const [editedAnswers, setEditedAnswers] = useState<{ [key: number]: string }>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchAnswerKey();
  }, [id]);

  const fetchAnswerKey = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/quizzes/${id}/answer-key`);
      setData(response.data);
      
      // Initialize editedAnswers with existing answer_key_text values
      const initialAnswers: { [key: number]: string } = {};
      response.data.questions.forEach((q: Question) => {
        initialAnswers[q.id] = q.answer_key_text || '';
      });
      setEditedAnswers(initialAnswers);
      setHasChanges(false);
    } catch (err: any) {
      console.error('Error fetching answer key:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch answer key';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: number, value: string) => {
    setEditedAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!data) return;
    
    try {
      setSaving(true);
      
      // Prepare answers array for API
      const answers = Object.entries(editedAnswers).map(([questionId, answerKeyText]) => ({
        question_id: parseInt(questionId),
        answer_key_text: answerKeyText || null
      }));
      
      await api.put(`/api/quizzes/${id}/answer-key`, { answers });
      
      showToast('Answer key saved successfully!', 'success');
      setHasChanges(false);
      
      // Refresh data to get updated state
      await fetchAnswerKey();
    } catch (err: any) {
      console.error('Error saving answer key:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save answer key';
      showToast(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="loading-state">
            <span className="loading-spinner"></span>
            Loading answer key...
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="page">
        <div className="container">
          <div className="alert alert-error">
            <strong>Error:</strong> {error || 'Answer key not found'}
          </div>
          <button 
            className="btn btn-outline"
            onClick={() => navigate('/admin')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="page-title">Answer Key: {data.quiz.title}</h1>
          <div className="d-flex gap-2">
            {hasChanges && (
              <button 
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
                style={{ backgroundColor: '#4CAF50', borderColor: '#4CAF50' }}
              >
                {saving ? 'Saving...' : 'Save Answer Key'}
              </button>
            )}
            <button 
              className="btn btn-outline"
              onClick={() => navigate('/admin')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {data.quiz.description && (
          <p style={{ color: '#666', marginBottom: '20px' }}>{data.quiz.description}</p>
        )}

        {/* Instructions */}
        <div className="alert" style={{ 
          backgroundColor: '#E3F2FD', 
          border: '1px solid #2196F3', 
          borderRadius: '6px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <strong>Instructions:</strong> Enter the correct answer for each question below. 
          For multiple choice questions, the correct options are already marked. 
          For text and audio questions, type the expected correct answer in the text field.
        </div>

        {/* Questions List */}
        <div className="card">
          <div className="card-header">
            <h2>Questions & Answers ({data.questions.length})</h2>
          </div>
          
          <div className="answers-list" style={{ padding: '15px' }}>
            {data.questions.map((question) => (
              <div 
                key={question.id} 
                className="card answer-item"
                style={{
                  marginBottom: '15px',
                  borderLeft: editedAnswers[question.id] 
                    ? '4px solid #4CAF50' 
                    : '4px solid #e0e0e0',
                  opacity: question.is_active ? 1 : 0.7
                }}
              >
                <div className="question-header">
                  <span className="question-number">Q{question.order_num}</span>
                  <span className="question-type-badge">{question.question_type.replace('_', ' ')}</span>
                  {!question.is_active && (
                    <span className="status-badge" style={{ backgroundColor: '#999', color: 'white', marginLeft: '8px' }}>
                      Inactive
                    </span>
                  )}
                </div>
                
                <p className="question-text" style={{ marginTop: '10px', marginBottom: '15px' }}>
                  <strong>{question.text}</strong>
                </p>

                {/* Question Images */}
                {question.images && question.images.length > 0 && (
                  <div className="question-images" style={{ marginBottom: '15px' }}>
                    {question.images.map((img, imgIndex) => (
                      <div key={imgIndex} style={{ marginBottom: '10px' }}>
                        <ImageViewer 
                          src={`${API_URL}/uploads/${img.image_file}`} 
                          title={img.caption || `Question Image ${imgIndex + 1}`}
                          maxHeight={200}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Answer Input/Display */}
                <div className="answer-content">
                  {question.question_type === 'multiple_choice' && (
                    <div>
                      <p style={{ fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>
                        Correct Option(s) {question.allow_multiple_answers ? '(Multiple)' : '(Single)'}:
                      </p>
                      <div className="options-list" style={{ marginBottom: '15px' }}>
                        {question.options.filter(opt => opt.is_correct).map((option) => (
                          <div 
                            key={option.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '10px 15px',
                              marginBottom: '8px',
                              borderRadius: '6px',
                              backgroundColor: '#E8F5E9',
                              border: '2px solid #4CAF50'
                            }}
                          >
                            <span style={{ marginRight: '10px', fontSize: '18px' }}>✅</span>
                            <span style={{ color: '#2E7D32', fontWeight: 'bold' }}>
                              {option.text}
                            </span>
                          </div>
                        ))}
                        {question.options.filter(opt => opt.is_correct).length === 0 && (
                          <div style={{ color: '#F44336', fontStyle: 'italic' }}>
                            No correct option marked
                          </div>
                        )}
                      </div>
                      
                      {/* Additional notes field for multiple choice */}
                      <div>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', color: '#666' }}>
                          Additional Notes (optional):
                        </label>
                        <textarea
                          value={editedAnswers[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          placeholder="Add any additional notes or explanation..."
                          style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #ddd',
                            minHeight: '60px',
                            fontSize: '14px',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {question.question_type === 'text' && (
                    <div>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#2E7D32' }}>
                        ✅ Correct Answer:
                      </label>
                      <textarea
                        value={editedAnswers[question.id] || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder="Enter the correct answer for this question..."
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '6px',
                          border: editedAnswers[question.id] ? '2px solid #4CAF50' : '1px solid #ddd',
                          backgroundColor: editedAnswers[question.id] ? '#E8F5E9' : 'white',
                          minHeight: '80px',
                          fontSize: '15px',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  )}

                  {question.question_type === 'audio_response' && (
                    <div>
                      <div style={{
                        backgroundColor: '#FFF3E0',
                        border: '1px solid #FFB74D',
                        borderRadius: '6px',
                        padding: '10px 15px',
                        marginBottom: '15px',
                        color: '#E65100'
                      }}>
                        🎤 Audio response question
                      </div>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#2E7D32' }}>
                        ✅ Expected Answer:
                      </label>
                      <textarea
                        value={editedAnswers[question.id] || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder="Describe what the correct verbal answer should include..."
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '6px',
                          border: editedAnswers[question.id] ? '2px solid #4CAF50' : '1px solid #ddd',
                          backgroundColor: editedAnswers[question.id] ? '#E8F5E9' : 'white',
                          minHeight: '80px',
                          fontSize: '15px',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating Save Button */}
        {hasChanges && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1000
          }}>
            <button 
              className="btn btn-primary btn-large"
              onClick={handleSave}
              disabled={saving}
              style={{ 
                backgroundColor: '#4CAF50', 
                borderColor: '#4CAF50',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                padding: '12px 24px',
                fontSize: '16px'
              }}
            >
              {saving ? 'Saving...' : 'Save Answer Key'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnswerKeyPage;
